import { useEffect, useMemo, useRef } from 'react';
import { useMultiTreeQuery } from 'components/GraphTree/queries';
import { buildTree } from 'components/GraphTree/TreeFuncs';
import _ from 'lodash';

const useArrayMemo = (array) => {
  // this holds reference to previous value of array
  const ref = useRef();
  // check if each element of the old and new array match
  const areArraysConsideredTheSame =
    ref.current && array.length === ref.current.length
      ? array.every((element, i) => {
        return element === ref.current[i];
      })
      // initially there's no old array defined/stored, so set to false
      : false;

  useEffect(() => {
    // only update prev results if array is not deemed the same
    if (!areArraysConsideredTheSame) {
      ref.current = array;
    }
  }, [areArraysConsideredTheSame, array]);
  return areArraysConsideredTheSame ? ref.current : array;
};

/**
 * Returns a list of nodes that are updated with the latest data from the tree query
 * @param {array} nodes
 * @returns
 */
export const useUpdatedNodes = (nodes) => {
  // All root ids that the static node selection uses
  const treeRootIds = useMemo(() => {
    return _.reduce(nodes, (acc, asset) => {
      if (asset?.treeRoot) {
        acc.push(asset.treeRoot._key);
      }
      return acc;
    }, []);
  }, [nodes]);

  const query = useMultiTreeQuery(treeRootIds);
  const isLoading = _.some(query, { isLoading: true });
  const queryData = useArrayMemo(_.map(query, 'data'));

  // All trees that the nodes uses
  const trees = useMemo(() => {
    return (isLoading ? [] : _.map(queryData, data => data ? buildTree(data) : {}));

  }, [isLoading, queryData]);

  const result = useMemo(() => {
    // We have selected static nodes, update node data from the downloaded trees
    const newNodes =
     _.map(nodes, node => {
       for (const tree of trees) {
         if (tree.id !== node.treeRoot?._key) {
           continue;
         }

         const foundNode = tree?.nodeMap?.[node.id];
         if (foundNode != null) {
           return foundNode;
         }
       }
       // Return the queried node if we can't find it in the tree
       // This is because we still want the old dashboards to work.
       return node;
     });
    return { nodes: newNodes, isLoading };
  }, [nodes, trees, isLoading]);
  return result;
};

export default useUpdatedNodes;
